import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { VendorType } from '@econtractor/types';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import VendorRowActions from '../VendorRowActions';

interface VendorsTableRowProps {
    vendor: VendorType;
    fetchVendors: (force?: boolean) => Promise<void>;
    setEditVendorId: (vendorId: number) => void;
    setSubjectsVendorId: (vendorId: number) => void;
    setPersonsVendorId: (vendorId: number) => void;
}

const websiteHref = (value?: string | null): string | null => {
    const website = String(value || '').trim();
    if (!website) return null;
    if (/^https?:\/\//i.test(website)) return website;
    return null;
};

const VendorsTableRow = ({
    vendor,
    fetchVendors,
    setEditVendorId,
    setSubjectsVendorId,
    setPersonsVendorId,
}: VendorsTableRowProps) => {
    const { t } = useTranslation(['Vendors']);
    const { auth } = useAuth();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const href = websiteHref(vendor.vendorWebsite);
    const website = String(vendor.vendorWebsite || '').trim();

    return (
        <tr>
            <td className="name">
                <h6
                    className={`mb-0 text-break${canEdit ? ' cursor-pointer' : ''}`}
                    onClick={() => {
                        if (canEdit) setEditVendorId(vendor.ID);
                    }}
                >
                    {vendor.vendorName || '-'}
                </h6>
            </td>
            <td className="phone text-break">
                <CopyTextWrapper value={vendor.vendorPhone || ''} wrap>
                    <span>{vendor.vendorPhone || '-'}</span>
                </CopyTextWrapper>
            </td>
            <td className="website text-break">
                {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {website}
                    </a>
                ) : (
                    website || '-'
                )}
            </td>
            <td className="products">{vendor.productsCount || 0}</td>
            {canEdit && (
                <td className="action">
                    <div className="d-flex align-items-center justify-content-end gap-1">
                        <EToolTip title={t('vendorPopup.subjects')}>
                            <span className="d-inline-block">
                                <Button
                                    className="p-1 py-0"
                                    size="sm"
                                    variant="light-secondary"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        setSubjectsVendorId(vendor.ID);
                                    }}
                                >
                                    <SolarIcon name="person-vcard" />
                                </Button>
                            </span>
                        </EToolTip>
                        <EToolTip title={t('vendorPopup.persons')}>
                            <span className="d-inline-block">
                                <Button
                                    className="p-1 py-0"
                                    size="sm"
                                    variant="light-secondary"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        setPersonsVendorId(vendor.ID);
                                    }}
                                >
                                    <SolarIcon name="people" />
                                </Button>
                            </span>
                        </EToolTip>
                        <VendorRowActions
                            vendor={vendor}
                            fetchVendors={fetchVendors}
                            setEditVendorId={setEditVendorId}
                            setSubjectsVendorId={setSubjectsVendorId}
                            setPersonsVendorId={setPersonsVendorId}
                        />
                    </div>
                </td>
            )}
        </tr>
    );
};

export default VendorsTableRow;
